import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { downloadBlob } from "../../nativeDownload";

const TEAM_MATCH_PLAY_FORMATS = ['Better Ball Match Play', 'Fourball Match Play', 'Foursomes Match Play', 'Greensomes Match Play', 'Team Match Play'];

// Match play never uses a side's/player's full allowance directly — the
// LOWEST allowance anywhere in the match plays off scratch, everyone else
// gets the difference, allocated by stroke index. Must match
// MatchPlayScoreEntry.jsx's calculation exactly. Percentages per R&A Rules
// of Handicapping Appendix C: Singles 100%, Fourball 90% each individually,
// Foursomes/Greensomes off each side's combined allowance (50% / 60-40).
function getMatchType(gameType) {
  // "Better Ball Match Play" is the same fourball mechanic as "Fourball
  // Match Play" (each partner plays their own ball, better one counts)
  // despite the different name — missing here until 2026-09-06, it was
  // silently scored as a singles 1v1 match instead.
  if (gameType === "Fourball Match Play" || gameType === "Better Ball Match Play") return "fourball";
  if (gameType === "Foursomes Match Play" || gameType === "Greensomes Match Play") return "alternate";
  return "singles";
}
function sideAllowance(side, matchType, gameType) {
  if (matchType === "alternate") {
    const combined = side.reduce((s, p) => s + ((p.hcp_adj ?? p.handicap) || 0), 0);
    if (gameType === "Greensomes Match Play") {
      const lower = Math.min(...side.map(p => (p.hcp_adj ?? p.handicap) || 0));
      const higher = Math.max(...side.map(p => (p.hcp_adj ?? p.handicap) || 0));
      return Math.round(0.6 * lower + 0.4 * higher);
    }
    return Math.round(combined * 0.5);
  }
  return (side[0]?.hcp_adj ?? side[0]?.handicap) || 0;
}
function playerAllowance(player, matchType) {
  const raw = (player?.hcp_adj ?? player?.handicap) || 0;
  return matchType === "fourball" ? Math.round(raw * 0.9) : raw;
}
function strokesFor(allowance, strokeIndex, lowBaseline) {
  const diff = Math.max(0, allowance - lowBaseline);
  const base = Math.floor(diff / 18);
  const extra = diff % 18;
  return base + (strokeIndex <= extra ? 1 : 0);
}
// Formats where nearest-pin prizes cleanly apply (per society rules) — excludes
// Foursomes/Greensomes Match Play, where alternate-shot play makes them a poor fit.
const PRIZE_ELIGIBLE_FORMATS = ['Singles Match Play', 'Better Ball Match Play', 'Fourball Match Play', 'Team Match Play'];

export default function MatchResults() {
  const [event, setEvent] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventMeta, setEventMeta] = useState({ isInterClub: false });
  const [toast, setToast] = useState("");
  const [prizes, setPrizes] = useState(null);
  const nearestPinRef = React.useRef("");
  const nearestPin2Ref = React.useRef("");
  const longestDriveRef = React.useRef("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setLoading(false); return; }
      setEvent(eventRes.data);
      const isInterClub = TEAM_MATCH_PLAY_FORMATS.includes(eventRes.data.game_type);

      const [scoresRes, groupsRes, holesRes, teamsRes, prizesRes] = await Promise.all([
        api.get("/scores/" + eventRes.data.id),
        api.get("/groups"),
        api.get("/courses/" + eventRes.data.course_id + "/holes"),
        api.get("/teams").catch(() => ({ data: { teams: [] } })),
        api.get("/events/prizes").catch(() => ({ data: { nearest_pin: "", nearest_pin2: "", longest_drive: "" } }))
      ]);
      nearestPinRef.current = prizesRes.data.nearest_pin || "";
      nearestPin2Ref.current = prizesRes.data.nearest_pin2 || "";
      longestDriveRef.current = prizesRes.data.longest_drive || "";
      setPrizes(prizesRes.data);

      const players = (groupsRes.data.players || []).filter(p => p.playing);
      const holes = holesRes.data;
      const scores = scoresRes.data;

      const teamNameMap = {};
      (teamsRes.data.teams || []).forEach(t => { teamNameMap[t.id] = t.team_name; });
      const clubName = (teamId) => teamNameMap[teamId] || ('Team ' + teamId);

      const matchType = getMatchType(eventRes.data.game_type);

      const grpMap = {};
      players.forEach(p => {
        if (!grpMap[p.grp]) grpMap[p.grp] = {};
        const tid = p.team_id || 0;
        if (!grpMap[p.grp][tid]) grpMap[p.grp][tid] = [];
        grpMap[p.grp][tid].push(p);
      });

      const matchResults = [];
      Object.entries(grpMap).sort((a,b) => parseInt(a[0])-parseInt(b[0])).forEach(([grp, teamGroups]) => {
        const teamIds = Object.keys(teamGroups);
        if (teamIds.length < 2) return;
        const sideATeamId = parseInt(teamIds[0]);
        const sideBTeamId = parseInt(teamIds[1]);
        const sideA = teamGroups[teamIds[0]];
        const sideB = teamGroups[teamIds[1]];
        const nameA = sideA.map(p => p.name.split(" ")[0]).join("/");
        const nameB = sideB.map(p => p.name.split(" ")[0]).join("/");
        const clubA = clubName(sideATeamId);
        const clubB = clubName(sideBTeamId);
        const teamAPlayerIds = sideA.map(p => p.player_id);
        const teamBPlayerIds = sideB.map(p => p.player_id);

        // Only the credited scorer has a non-zero gross for a given hole
        // (fourball: whichever partner had the better ball; singles/
        // alternate: the only contributor there is anyway).
        const scoresA = scores.filter(s => sideA.some(p => p.player_id === s.player_id) && s.gross);
        const scoresB = scores.filter(s => sideB.some(p => p.player_id === s.player_id) && s.gross);

        const lowBaseline = matchType === "alternate"
          ? Math.min(sideAllowance(sideA, matchType, eventRes.data.game_type), sideAllowance(sideB, matchType, eventRes.data.game_type))
          : matchType === "fourball"
            ? Math.min(...[...sideA, ...sideB].map(p => playerAllowance(p, matchType)))
            : Math.min(playerAllowance(sideA[0], matchType), playerAllowance(sideB[0], matchType));

        let scoreAUp = 0, scoreBUp = 0;
        let closedAt = null;

        holes.forEach((hole, i) => {
          if (closedAt !== null) return;
          const a = scoresA.find(s => s.hole === hole.hole);
          const b = scoresB.find(s => s.hole === hole.hole);
          if (!a || !b) return;

          const allowA = matchType === "alternate" ? sideAllowance(sideA, matchType, eventRes.data.game_type)
            : playerAllowance(sideA.find(p => p.player_id === a.player_id), matchType);
          const allowB = matchType === "alternate" ? sideAllowance(sideB, matchType, eventRes.data.game_type)
            : playerAllowance(sideB.find(p => p.player_id === b.player_id), matchType);
          const netA = parseInt(a.gross) - strokesFor(allowA, hole.stroke_index, lowBaseline);
          const netB = parseInt(b.gross) - strokesFor(allowB, hole.stroke_index, lowBaseline);
          if (netA < netB) scoreAUp++;
          else if (netB < netA) scoreBUp++;

          const lead = Math.abs(scoreAUp - scoreBUp);
          const holesLeft = holes.length - (i + 1);
          if (lead > holesLeft) closedAt = i + 1;
        });

        const lead = Math.abs(scoreAUp - scoreBUp);
        const leaderName = scoreAUp > scoreBUp ? nameA : scoreBUp > scoreAUp ? nameB : null;
        const winningSide = scoreAUp > scoreBUp ? 'A' : scoreBUp > scoreAUp ? 'B' : null;
        const leaderClub = winningSide === 'A' ? clubA : winningSide === 'B' ? clubB : null;

        const allHolesScored = scoresA.length === holes.length && scoresB.length === holes.length;
        const isComplete = closedAt !== null || allHolesScored;

        let margin = "";
        if (lead === 0 && isComplete) margin = "Halved";
        else if (lead === 0) margin = "All Square";
        else if (closedAt) {
          const remaining = holes.length - closedAt;
          margin = `${lead}&${remaining}`;
        } else if (isComplete) {
          margin = `${lead}UP`;
        } else {
          margin = `${lead}UP (in progress)`;
        }

        let result = "No scores";
        let clubResult = "No scores";
        if (scoresA.length > 0 && scoresB.length > 0) {
          if (margin === "Halved" || margin === "All Square") {
            result = margin;
            clubResult = margin;
          } else if (isComplete) {
            result = `${leaderName} wins ${margin}`;
            clubResult = `Winner: ${leaderClub} ${margin}`;
          } else {
            result = `${leaderName} ${margin}`;
            clubResult = `${leaderClub} ${margin}`;
          }
        }

        matchResults.push({
          grp: parseInt(grp), nameA, nameB, clubA, clubB, result, clubResult,
          winningSide, isComplete, margin, teamAPlayerIds, teamBPlayerIds
        });
      });

      setMatches(matchResults);
      setEventMeta({ isInterClub });
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { isInterClub } = eventMeta;

  const teamAName = matches[0]?.clubA || 'Team A';
  const teamBName = matches[0]?.clubB || 'Team B';

  let teamAWins = 0, teamBWins = 0, halved = 0;
  matches.forEach(m => {
    if (!m.isComplete) return;
    if (m.winningSide === 'A') teamAWins++;
    else if (m.winningSide === 'B') teamBWins++;
    else halved++;
  });

  const persistPrizes = async (confirm) => {
    try {
      await api.put("/events/prizes", {
        nearest_pin: nearestPinRef.current,
        nearest_pin2: nearestPin2Ref.current,
        longest_drive: longestDriveRef.current
      });
      if (confirm) showToast("Prizes saved!");
    } catch(e) { showToast("Failed to save prize: " + (e.response?.data?.error || e.message)); }
  };

  const archiveResults = async () => {
    if (!event) return;
    if (matches.length === 0) { showToast("No matches to archive"); return; }
    try {
      const archiveMatches = matches.map(m => ({
        grp: m.grp,
        team_a_player_ids: m.teamAPlayerIds,
        team_b_player_ids: m.teamBPlayerIds,
        team_a_players: m.nameA,
        team_b_players: m.nameB,
        winning_side: m.winningSide,
        margin: m.margin
      }));
      await api.post("/archive/save", {
        event_id: event.id,
        results: [],
        matches: archiveMatches,
        team_a_name: isInterClub ? teamAName : '',
        team_b_name: isInterClub ? teamBName : '',
        team_a_wins: isInterClub ? teamAWins : 0,
        team_b_wins: isInterClub ? teamBWins : 0,
        matches_halved: isInterClub ? halved : 0,
        nearest_pin: nearestPinRef.current,
        nearest_pin2: nearestPin2Ref.current,
        longest_drive: longestDriveRef.current
      });
      await api.put("/events/prizes", {
        nearest_pin: nearestPinRef.current,
        nearest_pin2: nearestPin2Ref.current,
        longest_drive: longestDriveRef.current
      });
      showToast("Results archived!");
    } catch(e) { showToast("Failed: " + e.message); }
  };

  // Returns "X lost to Y margin" style line for PDF, using player pair names (per spec example)
  const matchLine = (m) => {
    if (m.margin === "Halved" || m.margin === "All Square") {
      return `${m.nameA} halved with ${m.nameB}`;
    }
    if (m.winningSide === 'A') return `${m.nameB} lost to ${m.nameA} ${m.margin}`;
    if (m.winningSide === 'B') return `${m.nameA} lost to ${m.nameB} ${m.margin}`;
    return `${m.nameA} vs ${m.nameB} — ${m.margin}`;
  };

  const exportToExcel = () => {
    if (!event || matches.length === 0) { showToast("No matches to export"); return; }
    const sheetData = matches.map(m => ({
      Group: m.grp,
      "Team A": m.nameA,
      "Team B": m.nameB,
      Result: isInterClub ? m.clubResult : m.result
    }));
    if (isInterClub) {
      sheetData.unshift({
        Group: "",
        "Team A": teamAName,
        "Team B": teamBName,
        Result: `Final Score: ${teamAName} ${teamAWins} - ${teamBWins} ${teamBName}${halved > 0 ? ` (${halved} halved)` : ''}`
      });
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetData), "Match Results");
    downloadBlob(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${event.game_type.replace(/\s+/g, '_')}_Results.xlsx`);
  };

  const exportToPDF = () => {
    if (!event || matches.length === 0) { showToast("No matches to export"); return; }
    const doc = new jsPDF();
    let y = 20;

    if (isInterClub) {
      doc.setFontSize(16);
      doc.text(`${teamAName.toUpperCase()} v ${teamBName.toUpperCase()}`, 14, y);
      y += 10;
      doc.setFontSize(12);
      doc.text(`Final Score: ${teamAName.toUpperCase()} ${teamAWins} - ${teamBWins} ${teamBName.toUpperCase()}${halved > 0 ? ` (${halved} halved)` : ''}`, 14, y);
      y += 12;
    } else {
      doc.setFontSize(16);
      doc.text(event.game_type, 14, y);
      y += 12;
    }

    doc.setFontSize(13);
    doc.text("Match Results", 14, y);
    y += 6;
    doc.setLineWidth(0.5);
    doc.line(14, y, 80, y);
    y += 8;

    doc.setFontSize(11);
    matches.forEach(m => {
      if (y > 280) { doc.addPage(); y = 20; }
      doc.text(matchLine(m), 14, y);
      y += 8;
    });

    downloadBlob(doc.output("blob"), `${event.game_type.replace(/\s+/g, '_')}_Results.pdf`);
  };

  return (
    <Layout>
      {toast && <div style={{position:"fixed",bottom:"20px",right:"20px",zIndex:9999}} className="bg-green-600 text-white px-4 py-3 rounded shadow-lg text-sm font-medium">{toast}</div>}
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm">Match Play Results</span>
          {event && <span className="text-xs text-blue-600 font-medium">{event.game_type}</span>}
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {PRIZE_ELIGIBLE_FORMATS.includes(event?.game_type) && (
              <>
                <div className="flex flex-col items-center">
                  <label className="text-xs font-bold text-gray-600 mb-0.5">Nearest Pin</label>
                  <input key={"np-" + (prizes?.nearest_pin || "")} type="text" defaultValue={prizes?.nearest_pin || ""} onChange={e => nearestPinRef.current = e.target.value} onBlur={persistPrizes} placeholder="Player name" className="border-2 border-green-500 rounded px-2 py-1 text-sm font-semibold w-32" />
                </div>
                <div className="flex flex-col items-center">
                  <label className="text-xs font-bold text-gray-600 mb-0.5">Nearest Pin 2</label>
                  <input key={"np2-" + (prizes?.nearest_pin2 || "")} type="text" defaultValue={prizes?.nearest_pin2 || ""} onChange={e => nearestPin2Ref.current = e.target.value} onBlur={persistPrizes} placeholder="Player name" className="border-2 border-green-500 rounded px-2 py-1 text-sm font-semibold w-32" />
                </div>
                <button type="button" onClick={() => persistPrizes(true)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded self-end">💾 Save Prizes</button>
              </>
            )}
            <button type="button" onClick={exportToExcel} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📊 Export Excel</button>
            <button type="button" onClick={exportToPDF} className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📄 Export PDF</button>
            <button type="button" onClick={archiveResults} className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold px-3 py-1.5 rounded">Archive Results</button>
          </div>
        </div>
        {isInterClub && matches.length > 0 && (
          <div className="bg-green-800 text-white px-4 py-3 text-center font-bold text-lg">
            {teamAName.toUpperCase()} {teamAWins} - {teamBWins} {teamBName.toUpperCase()}
            {halved > 0 && <span className="text-sm font-normal"> &nbsp;({halved} halved)</span>}
          </div>
        )}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-green-800 text-white">
              <tr>
                <th className="px-4 py-2 text-center w-16">Group</th>
                <th className="px-4 py-2 text-left">Match</th>
                <th className="px-4 py-2 text-left">Result</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m, i) => (
                <tr key={m.grp} className={"border-b border-gray-200 " + (i%2===0 ? "bg-white" : "bg-green-50")}>
                  <td className="px-4 py-2 text-center font-bold">{m.grp}</td>
                  <td className="px-4 py-2">
                    {isInterClub ? (
                      <span>{m.nameA} ({m.clubA})  vs  {m.nameB} ({m.clubB})</span>
                    ) : (
                      <span>{m.nameA} vs {m.nameB}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 font-bold text-blue-700">
                    {isInterClub ? m.clubResult : m.result}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && matches.length === 0 && (
            <div className="text-center py-8 text-gray-500">No matches found.</div>
          )}
        </div>
      </div>
    </Layout>
  );
}
